<template>
  <div class="color-wheel-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Congratulations!"
    />
    <div v-else>
      <!-- Draggable Items (Colors) -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
      <div class="draggable-items">
        <div
          v-for="(color, index) in shuffledColors"
          :key="'color-' + index"
          class="item-draggable"
          :style="{ backgroundColor: color.colorValue }"
          draggable="true"
          @dragstart="handleDragStart($event, color)"
          @click="speakOutLoud(color.colorName)"
          @touchstart.stop.prevent="handleTouchStart($event, color)"
          @touchmove.stop.prevent="handleTouchMove($event)"
          @touchend.stop.prevent="handleTouchEnd($event)"
        >
          {{ color.colorName }}
        </div>
      </div>

      <!-- Circle Slots (Emojis) -->
      <div class="circle-container">
        <div
          v-for="(item, index) in items"
          :key="'slot-' + index"
          class="circle-slot"
          :style="getSlotStyle(index, item)"
          :data-index="index"
          @dragover.prevent
          @drop="handleDrop($event, item)"
          @click="speakOutLoud(item.itemName)"
        >
          <span
            :style="{
              color: item.matched ? 'black' : 'gray',
              fontSize: '2rem',
            }"
          >
            {{ item.emoji }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      colors: [
        {
          colorName: "Red",
          colorValue: "#FF0000",
        },
        {
          colorName: "Orange",
          colorValue: "#FFA500",
        },
        {
          colorName: "Yellow",
          colorValue: "#FFFF00",
        },
        {
          colorName: "Green",
          colorValue: "#008000",
        },
        {
          colorName: "Blue",
          colorValue: "#0000FF",
        },
        {
          colorName: "Purple",
          colorValue: "#800080",
        },
      ],
      items: [
        {
          itemName: "Cherry",
          emoji: "🍒",
          colorValue: "#FF0000",
          matched: false,
        }, // Red
        {
          itemName: "Orange",
          emoji: "🍊",
          colorValue: "#FFA500",
          matched: false,
        }, // Orange
        {
          itemName: "Banana",
          emoji: "🍌",
          colorValue: "#FFFF00",
          matched: false,
        }, // Yellow
        {
          itemName: "Green Apple",
          emoji: "🍏",
          colorValue: "#008000",
          matched: false,
        }, // Green
        {
          itemName: "Blue Car",
          emoji: "🚙",
          colorValue: "#0000FF",
          matched: false,
        }, // Blue
        {
          itemName: "Grape",
          emoji: "🍇",
          colorValue: "#800080",
          matched: false,
        }, // Purple
      ],
      shuffledColors: [],
      draggedItem: null,
      message: "",
      isGameComplete: false,
      touchX: 0,
      touchY: 0,
      draggedElement: null, // Added for touch dragging
    };
  },
  mounted() {
    this.setupGame();
    // Set up speech voices from the mixin
    this.setupVoices();
  },
  methods: {
    setupGame() {
      this.shuffledColors = this.shuffleArray([...this.colors]);
    },
    shuffleArray(array) {
      return array.sort(() => Math.random() - 0.5);
    },
    handleDragStart(event, color) {
      event.dataTransfer.setData("text/plain", color.colorValue);
      this.draggedItem = color;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    handleDrop(event, targetItem) {
      const draggedColorValue = event.dataTransfer.getData("text/plain");
      if (
        this.draggedItem &&
        this.draggedItem.colorValue === draggedColorValue
      ) {
        if (this.draggedItem.colorValue === targetItem.colorValue) {
          if (!targetItem.matched) {
            targetItem.matched = true;
            this.message = "✅ Correct!";
            this.playSound(
              "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
            );
            // Speak the color name using the mixin's method
            this.speakOutLoud(this.draggedItem.colorName);
            this.draggedItem = null;
            if (this.items.every((item) => item.matched)) {
              this.isGameComplete = true;
              this.speakOutLoud("Congratulations!");
              setTimeout(() => {
                this.$router.push("/colors");
              }, 3000);
            }
          } else {
            this.message = "❌ Already matched!";
          }
        } else {
          this.message = "❌ Try Again!";
        }
      }
    },
    resetGame() {
      this.items.forEach((item) => (item.matched = false));
      this.setupGame();
      this.isGameComplete = false;
      this.message = "";
    },
    getSlotStyle(index, item) {
      const totalItems = this.items.length;
      const angle = ((index / totalItems) * 360 - 90) % 360; // Start from top
      const angleInRadians = (angle * Math.PI) / 180;

      // Using percentages for responsiveness
      const radius = 40; // Percentage radius from center
      const centerX = 50; // Center in percentage
      const centerY = 50; // Center in percentage

      const x = centerX + radius * Math.cos(angleInRadians);
      const y = centerY + radius * Math.sin(angleInRadians);

      const backgroundColor = item.matched ? item.colorValue : "lightgray";

      return {
        left: `${x}%`,
        top: `${y}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: backgroundColor,
        color: item.matched ? "black" : "gray",
      };
    },
    // Touch event handlers for mobile devices
    handleTouchStart(event, color) {
      event.preventDefault();
      this.draggedItem = color;
      this.touchX = event.touches[0].clientX;
      this.touchY = event.touches[0].clientY;

      // Create visual representation
      const originalElement = event.currentTarget;
      this.draggedElement = originalElement.cloneNode(true);
      this.draggedElement.style.position = "fixed";
      this.draggedElement.style.left = `${this.touchX - 40}px`; // Centering the element
      this.draggedElement.style.top = `${this.touchY - 40}px`; // Centering the element
      this.draggedElement.style.zIndex = 1000;
      this.draggedElement.style.pointerEvents = "none";
      this.draggedElement.style.touchAction = "none"; // Prevent default touch actions

      document.body.appendChild(this.draggedElement);
    },
    handleTouchMove(event) {
      event.preventDefault();
      const touch = event.touches[0];
      this.touchX = touch.clientX;
      this.touchY = touch.clientY;

      if (this.draggedElement) {
        // Update position of dragged element
        this.draggedElement.style.left = `${this.touchX - 40}px`;
        this.draggedElement.style.top = `${this.touchY - 40}px`;
      }
    },
    handleTouchEnd(event) {
      event.preventDefault();
      if (this.draggedElement && this.draggedElement.parentNode) {
        this.draggedElement.parentNode.removeChild(this.draggedElement);
        this.draggedElement = null;
      }

      const touch = event.changedTouches[0];
      const dropTarget = document.elementFromPoint(
        touch.clientX,
        touch.clientY
      );
      if (dropTarget) {
        const slotElement = dropTarget.closest(".circle-slot");
        if (slotElement) {
          const slotIndex = parseInt(slotElement.getAttribute("data-index"));
          const targetItem = this.items[slotIndex];
          this.handleTouchDrop(targetItem);
        } else {
          this.message = "❌ Try Again!";
        }
      }
      this.draggedItem = null;
    },
    handleTouchDrop(targetItem) {
      // Similar to handleDrop logic
      if (
        this.draggedItem &&
        this.draggedItem.colorValue === targetItem.colorValue
      ) {
        if (!targetItem.matched) {
          targetItem.matched = true;
          this.message = "✅ Correct!";
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          // Speak the color name using the mixin's method
          this.speakOutLoud(this.draggedItem.colorName);
          if (this.items.every((item) => item.matched)) {
            this.isGameComplete = true;
            this.speakOutLoud("Congratulations!");
            setTimeout(() => {
              this.resetGame();
            }, 4000);
          }
        } else {
          this.message = "❌ Already matched!";
        }
      } else {
        this.message = "❌ Try Again!";
      }
    },
  },
};
</script>

<style scoped>
.color-wheel-game {
  text-align: center;
}

.draggable-items {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 40px;
}

.item-draggable {
  cursor: grab;
  border-radius: 10px;
  width: 80px;
  height: 80px;
  font-size: 1rem; /* Size for the text */
  margin: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: -2px 2px 2px 2px lightgray;
  touch-action: none; /* Prevent default touch behaviors */
  transition: transform 0.2s;
}

.item-draggable:active {
  cursor: grabbing;
}

.item-draggable:hover {
  transform: scale(1.1);
}

.circle-container {
  position: relative;
  width: 90%;
  max-width: 400px;
  aspect-ratio: 1 / 1;
  margin: 0 auto;
}

.circle-slot {
  position: absolute;
  width: 20%;
  height: 20%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid gray;
  box-shadow: 2px 2px 2px 2px lightgray;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
}

.message {
  font-size: 2rem;
  font-weight: bold;
  margin-top: 20px;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .circle-slot {
    width: 25%;
    height: 25%;
  }
  .message {
    font-size: 1.5rem;
  }
}

@media (max-width: 400px) {
  .circle-slot {
    width: 30%;
    height: 30%;
  }
  .message {
    font-size: 1.2rem;
  }
}
</style>
